// Transform AI feedback into structured dashboard data
export interface ValidationData {
  idea: {
    title: string;
    description: string;
    target_market: string;
  };
  swot: {
    strengths: string[];
    weaknesses: string[];
    opportunities: string[];
    threats: string[];
  };
  critical_questions: Array<{
    question: string;
    importance: 'high' | 'medium' | 'low';
  }>;
  competition: Array<{
    name: string;
    description: string;
    differentiators: string[];
  }>;
  action_plan: Array<{
    step: string;
    timeline: string;
    priority: 'high' | 'medium' | 'low';
    completed?: boolean;
  }>;
  market_demand: {
    size: 'large' | 'medium' | 'small';
    growth: 'high' | 'medium' | 'low';
    indicators: string[];
  };
  frameworks: {
    lean_startup: string;
    customer_development: string;
    mvp: string;
  };
}

export interface IdeaFormData {
  idea_description: string;
  target_audience: string;
  problem_solved: string;
  revenue_model: string;
  competition: string;
  why_pay: string;
}

export interface AIFeedbackRaw {
  strengths?: string[];
  weaknesses?: string[];
  opportunities?: string[];
  threats?: string[];
  risks?: string[];
  questions?: string[];
  decision?: string;
  cashpurr_commentary?: string;
  ratings?: {
    profitability: 'High' | 'Medium' | 'Low';
    urgency: 'High' | 'Medium' | 'Low';
    willingness_to_pay: 'High' | 'Medium' | 'Low';
    market_size: 'High' | 'Medium' | 'Low';
  };
}

export function transformToValidationData(
  ideaData: IdeaFormData,
  aiFeedback?: AIFeedbackRaw
): ValidationData {
  // Extract competitor names from the competition field
  const competitorNames = ideaData.competition.split(',').map(c => c.trim()).filter(Boolean);
  
  return {
    idea: {
      title: ideaData.idea_description.split('.')[0] || ideaData.idea_description.substring(0, 50) + '...',
      description: ideaData.idea_description,
      target_market: ideaData.target_audience,
    },
    swot: {
      strengths: aiFeedback?.strengths || [
        'Unique value proposition that focuses specifically on the needs of solopreneurs and indiehackers',
        'Integration options with personal websites, providing increased visibility and customization for founders',
        'Ability to choose from multiple themes to fit the house style of each startup'
      ],
      weaknesses: aiFeedback?.weaknesses || [
        'Potential competition from existing social platforms and collaboration tools',
        'Initial challenge of attracting a critical mass of users to make the platform valuable',
        'Technical complexities in implementing integration options and maintaining compatibility with various website platforms'
      ],
      opportunities: aiFeedback?.opportunities || [
        'Growing market of solopreneurs and indiehackers seeking more customized and startup-focused platforms',
        'Potential partnerships with startup communities, accelerators, and incubators to promote the platform',
        'Expansion of features beyond updates and progress sharing to provide additional value for founders'
      ],
      threats: aiFeedback?.threats || [
        'Emergence of new competitors with similar or improved features',
        'User resistance to adopting yet another platform for sharing startup updates',
        'Potential for third-party integrations and potential issues with reliability or compatibility'
      ],
    },
    critical_questions: aiFeedback?.questions?.map((q, i) => ({
      question: q,
      importance: i < 3 ? 'high' as const : i < 5 ? 'medium' as const : 'low' as const
    })) || [
      {
        question: 'What is the current pain point faced by solopreneurs and indiehackers when it comes to sharing startup updates and progress?',
        importance: 'high' as const
      },
      {
        question: 'How do you plan to differentiate your platform from other existing tools that allow founders to share updates?',
        importance: 'high' as const
      },
      {
        question: 'What are the key features and integrations that solopreneurs and indiehackers would find most valuable in a platform like this?',
        importance: 'high' as const
      },
      {
        question: 'Have you conducted any surveys or interviews with your target market to gather feedback on the idea?',
        importance: 'medium' as const
      },
      {
        question: 'How do you plan to monetize this platform?',
        importance: 'medium' as const
      },
      {
        question: 'What is the estimated market size for your target audience?',
        importance: 'medium' as const
      },
      {
        question: 'How do you envision overcoming the initial challenge of attracting users to your platform?',
        importance: 'high' as const
      }
    ],
    competition: competitorNames.length > 0 ? competitorNames.map((name, index) => ({
      name: name,
      description: getCompetitorDescription(name),
      differentiators: getCompetitorDifferentiators(name)
    })) : [
      {
        name: 'Indie Hackers',
        description: 'An online community for indie hackers to share their projects and progress, with a focus on interviews and case studies.',
        differentiators: [
          'Customization options and a live feed integration for founder\'s own website',
          'Focus on solopreneurs and indiehackers, with more targeted features and themes'
        ]
      },
      {
        name: 'Product Hunt',
        description: 'A platform for discovering new products and startups, allowing founders to showcase their launches.',
        differentiators: [
          'Integration options with personal websites to embed live updates',
          'Focused on early adopters and trend-setters'
        ]
      },
      {
        name: 'StartupThreads',
        description: 'A service that provides custom-branded merchandise for startups to promote their brand.',
        differentiators: [
          'Customization options for founders to showcase their startup\'s progress and milestones',
          'Ability to integrate with own website and embed live updates into their own application'
        ]
      }
    ],
    action_plan: [
      {
        step: 'Conduct in-depth interviews with solopreneurs and indiehackers to gather feedback on pain points, desired features, and willingness to adopt the platform.',
        timeline: 'Week 1-2',
        priority: 'high' as const,
        completed: false
      },
      {
        step: 'Develop a landing page with key value propositions and a sign-up form to gauge interest and collect email addresses of potential users.',
        timeline: 'Week 2-3',
        priority: 'high' as const,
        completed: false
      },
      {
        step: 'Create wireframes and mockups for the platform\'s core features and user interface.',
        timeline: 'Week 3-4',
        priority: 'high' as const,
        completed: false
      },
      {
        step: 'Build a minimum viable product (MVP) with essential features like user profiles, project updates, and basic integration capabilities.',
        timeline: 'Week 4-8',
        priority: 'high' as const,
        completed: false
      },
      {
        step: 'Launch a beta version with a small group of target users to gather feedback and iterate on the product.',
        timeline: 'Week 8-10',
        priority: 'medium' as const,
        completed: false
      },
      {
        step: 'Develop partnerships with startup communities and accelerators to increase visibility and user acquisition.',
        timeline: 'Week 10-12',
        priority: 'medium' as const,
        completed: false
      }
    ],
    market_demand: {
      size: aiFeedback?.ratings?.market_size === 'High' ? 'large' : 
            aiFeedback?.ratings?.market_size === 'Medium' ? 'medium' : 'small',
      growth: aiFeedback?.ratings?.urgency === 'High' ? 'high' : 
              aiFeedback?.ratings?.urgency === 'Medium' ? 'medium' : 'low',
      indicators: [
        'Growing market of solopreneurs and indiehackers seeking more customized platforms',
        'Increased interest in startup progress and insights creates an opportunity',
        'Existing collaboration tools and social platforms do not offer sufficient customization options for startups'
      ]
    },
    frameworks: {
      lean_startup: 'Use the Build-Measure-Learn feedback loop to quickly iterate and validate assumptions about your product.',
      customer_development: 'Engage with potential customers to understand their needs, pain points, and validate your solution.',
      mvp: 'Develop a simplified version of your product with core features to gather user feedback and validate demand.'
    }
  };
}

function getCompetitorDescription(name: string): string {
  const descriptions: Record<string, string> = {
    'Twitter': 'Social media platform for sharing updates and building audience',
    'LinkedIn': 'Professional networking platform with business focus',
    'Product Hunt': 'Platform for discovering and launching new products',
    'Indie Hackers': 'Community for independent entrepreneurs and makers',
    'AngelList': 'Platform connecting startups with investors and talent',
    'Crunchbase': 'Database of companies, funding, and business information'
  };
  
  return descriptions[name] || `A platform competing in the ${name.toLowerCase()} space.`;
}

function getCompetitorDifferentiators(name: string): string[] {
  const differentiators: Record<string, string[]> = {
    'Twitter': [
      'Customization options and live feed integration for founder\'s own website',
      'Focus on solopreneurs and indiehackers with more targeted features'
    ],
    'LinkedIn': [
      'Integration options with personal websites to embed live updates',
      'Focused on startup progress rather than general professional networking'
    ],
    'Product Hunt': [
      'Integration options with personal websites to embed live updates',
      'Focused on early adopters and trend-setters'
    ]
  };
  
  return differentiators[name] || [
    'More targeted features for startup founders',
    'Better integration capabilities with personal websites'
  ];
}
